import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";

const T = {
  en: {
    title: "Simo Concierge",
    sub: "Here to help — instantly",
    greet: "Hello — I'm the Simo concierge. Would you like help finding the right pathway, estimating tuition, or beginning an application?",
    placeholder: "Ask anything about Simo…",
    thinking: "Typing…",
    quick: ["Which pathway suits my child?", "How much is tuition?", "How do I apply?"],
  },
  fr: {
    title: "Concierge Simo",
    sub: "À votre écoute — instantanément",
    greet: "Bonjour — je suis le concierge Simo. Souhaitez-vous de l'aide pour trouver le bon parcours, estimer les frais ou commencer une candidature ?",
    placeholder: "Posez toute question sur Simo…",
    thinking: "Rédaction…",
    quick: ["Quel parcours pour mon enfant ?", "Quels sont les frais ?", "Comment postuler ?"],
  },
  es: {
    title: "Conserje Simo",
    sub: "Aquí para ayudar — al instante",
    greet: "Hola — soy el conserje de Simo. ¿Quieres ayuda para encontrar el itinerario adecuado, estimar la matrícula o iniciar una solicitud?",
    placeholder: "Pregunta lo que quieras sobre Simo…",
    thinking: "Escribiendo…",
    quick: ["¿Qué itinerario para mi hijo?", "¿Cuánto cuesta la matrícula?", "¿Cómo solicito?"],
  },
  de: {
    title: "Simo-Concierge",
    sub: "Sofort für Sie da",
    greet: "Hallo — ich bin der Simo-Concierge. Möchten Sie Hilfe beim passenden Weg, bei der Gebührenschätzung oder beim Start einer Bewerbung?",
    placeholder: "Fragen Sie alles über Simo…",
    thinking: "Schreibt…",
    quick: ["Welcher Weg für mein Kind?", "Wie hoch sind die Gebühren?", "Wie bewerbe ich mich?"],
  },
};

const LEAD = {
  en: { cta: "Have an advisor contact me", name: "Your name", email: "Email", phone: "Phone / WhatsApp (optional)", send: "Send my details", sent: "Thank you — an admissions advisor will be in touch shortly." },
  fr: { cta: "Être recontacté par un conseiller", name: "Votre nom", email: "E-mail", phone: "Téléphone / WhatsApp (facultatif)", send: "Envoyer mes coordonnées", sent: "Merci — un conseiller aux admissions vous recontactera très bientôt." },
  es: { cta: "Que un asesor me contacte", name: "Tu nombre", email: "Correo electrónico", phone: "Teléfono / WhatsApp (opcional)", send: "Enviar mis datos", sent: "Gracias — un asesor de admisiones se pondrá en contacto pronto." },
  de: { cta: "Von einem Berater kontaktiert werden", name: "Ihr Name", email: "E-Mail", phone: "Telefon / WhatsApp (optional)", send: "Meine Daten senden", sent: "Danke — ein Zulassungsberater meldet sich in Kürze." },
};

const WA = {
  number: "14154297403",
  en: { cta: "Continue on WhatsApp", fab: "WhatsApp", lead_head: "Before we begin — who should we follow up with?", skip: "Skip for now", hello: (n) => `Hello Simo, I'm ${n || "a prospective applicant"}. I'd like to speak with an advisor.` },
  fr: { cta: "Continuer sur WhatsApp", fab: "WhatsApp", lead_head: "Avant de commencer — qui devons-nous recontacter ?", skip: "Ignorer pour l'instant", hello: (n) => `Bonjour Simo, je suis ${n || "un candidat"}. Je souhaite parler à un conseiller.` },
  es: { cta: "Continuar en WhatsApp", fab: "WhatsApp", lead_head: "Antes de empezar — ¿a quién contactamos?", skip: "Omitir por ahora", hello: (n) => `Hola Simo, soy ${n || "un candidato"}. Me gustaría hablar con un asesor.` },
  de: { cta: "Auf WhatsApp fortfahren", fab: "WhatsApp", lead_head: "Bevor wir beginnen — wen sollen wir kontaktieren?", skip: "Vorerst überspringen", hello: (n) => `Hallo Simo, ich bin ${n || "ein Interessent"}. Ich möchte mit einem Berater sprechen.` },
};

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

export function Concierge() {
  const { lang } = useI18n();
  const t = T[lang] || T.en;
  const l = LEAD[lang] || LEAD.en;
  const w = WA[lang] || WA.en;
  const [open, setOpen] = useState(false);
  const [greeted, setGreeted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sid, setSid] = useState(null);
  const [nudge, setNudge] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [leadRef, setLeadRef] = useState(null);
  const [offerWa, setOfferWa] = useState(false);
  const endRef = useRef(null);

  const waHref = () => {
    const parts = [w.hello(lead.name.trim())];
    if (leadRef) parts.push(`Ref: ${leadRef}.`);
    parts.push(`Lang: ${lang.toUpperCase()}.`);
    parts.push(`Page: ${window.location.pathname}`);
    return `https://wa.me/${WA.number}?text=${encodeURIComponent(parts.join(" "))}`;
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  // Proactive behaviour: gently surface a nudge once per session after browsing.
  useEffect(() => {
    if (sessionStorage.getItem("simo_concierge_seen")) return;
    const timer = setTimeout(() => {
      if (!open) { setNudge(true); sessionStorage.setItem("simo_concierge_seen", "1"); }
    }, 16000);
    const onExit = (e) => {
      if (e.clientY <= 0 && !open && !sessionStorage.getItem("simo_concierge_seen")) {
        setNudge(true); sessionStorage.setItem("simo_concierge_seen", "1");
      }
    };
    document.addEventListener("mouseout", onExit);
    return () => { clearTimeout(timer); document.removeEventListener("mouseout", onExit); };
  }, [open]);

  const openPanel = () => {
    setNudge(false); setOpen(true);
    if (!greeted) {
      setGreeted(true);
      setMessages([{ role: "assistant", text: t.greet }]);
      if (!leadSent) setLeadOpen(true);
    }
  };

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || loading) return;
    setMessages((m) => [...m, { role: "user", text: message }]);
    setInput(""); setLoading(true);
    try {
      const _path = window.location.pathname;
      const _prog = _path.startsWith("/programmes/") ? _path.split("/programmes/")[1] : undefined;
      const { data } = await api.post("/concierge", { message, session_id: sid, lang, page: _path, programme: _prog });
      setSid(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (data.offer_whatsapp) setOfferWa(true);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiErrorDetail(err.response?.data?.detail), error: true }]);
    } finally { setLoading(false); }
  };

  const submitLead = async () => {
    if (!lead.name.trim() || !lead.email.trim()) return;
    try {
      const transcript = messages.filter((m) => m.role === "user").map((m) => m.text).join(" | ").slice(0, 300);
      const { data } = await api.post("/concierge/lead", { ...lead, lang, page: window.location.pathname, message: transcript });
      setLeadRef(data.reference);
      setLeadSent(true);
    } catch (err) { /* keep panel open; non-blocking */ }
  };

  return (
    <>
      {/* Proactive nudge bubble */}
      <AnimatePresence>
        {nudge && !open && (
          <motion.button
            data-testid="concierge-nudge" onClick={openPanel}
            initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }}
            className="fixed bottom-24 right-5 z-[60] max-w-xs text-left bg-paper border border-border shadow-2xl rounded-sm p-4 pr-9">
            <div className="eyebrow text-heraldic mb-1 flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> {t.title}</div>
            <p className="text-sm text-ink/80 leading-relaxed">{t.greet}</p>
            <span onClick={(e) => { e.stopPropagation(); setNudge(false); }} className="absolute top-2 right-2 text-ink/40 hover:text-ink"><X className="h-4 w-4" /></span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Floating WhatsApp button — compact, sits left of the concierge launcher */}
      <a data-testid="whatsapp-fab" href={waHref()} target="_blank" rel="noopener noreferrer"
        aria-label={w.fab}
        className="fixed bottom-5 right-[5.5rem] z-[60] h-14 w-14 rounded-full bg-[#25D366] text-white shadow-2xl flex items-center justify-center hover:brightness-95 transition-all">
        <WhatsAppIcon className="h-7 w-7" />
      </a>

      {/* Launcher */}
      <button data-testid="concierge-launcher" onClick={() => (open ? setOpen(false) : openPanel())}
        aria-label={t.title}
        className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-heraldic text-white shadow-2xl flex items-center justify-center hover:bg-heraldic-700 transition-colors">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="concierge-panel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-5 z-[60] w-[min(92vw,380px)] h-[min(70vh,560px)] bg-paper border border-border shadow-2xl rounded-sm flex flex-col overflow-hidden">
            <div className="bg-ink text-paper px-5 py-4">
              <div className="font-serif text-xl leading-none">{t.title}</div>
              <div className="text-xs text-paper/55 mt-1">{t.sub}</div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} data-testid={`concierge-msg-${m.role}`}
                  className={`text-sm leading-relaxed rounded-sm px-3.5 py-2.5 max-w-[88%] ${m.role === "user" ? "ml-auto bg-heraldic/12 border border-heraldic/25 text-ink" : m.error ? "bg-red-50 border border-red-200 text-red-700" : "bg-secondary/70 border border-border text-ink/85"}`}>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
              ))}
              {messages.length === 1 && !loading && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {t.quick.map((q) => (
                    <button key={q} data-testid="concierge-quick" onClick={() => send(q)}
                      className="text-xs border border-border rounded-full px-3 py-1 text-ink/70 hover:border-heraldic hover:text-heraldic transition-colors">{q}</button>
                  ))}
                </div>
              )}
              {loading && <div className="flex items-center gap-2 text-ink/45 text-xs"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.thinking}</div>}
              {offerWa && (
                <a data-testid="concierge-whatsapp-cta" href={waHref()} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#25D366] text-white text-sm rounded-sm px-3.5 py-2.5 hover:brightness-95 transition-all">
                  <WhatsAppIcon className="h-4 w-4" /> {w.cta}
                </a>
              )}
              <div ref={endRef} />
            </div>
            {/* Lead capture — hand off straight to admissions */}
            {leadSent ? (
              <div data-testid="concierge-lead-sent" className="border-t border-border px-4 py-3 text-sm text-heraldic bg-heraldic/5">{l.sent}{leadRef && <span className="block mt-1 font-mono text-xs text-ink/60">{leadRef}</span>}</div>
            ) : leadOpen ? (
              <div data-testid="concierge-lead-form" className="border-t border-border p-3 space-y-2 bg-secondary/40">
                <div className="text-xs text-ink/60 leading-snug">{w.lead_head}</div>
                <input data-testid="lead-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder={l.name}
                  className="w-full text-sm bg-paper border border-border rounded-sm px-3 py-2 focus:outline-none focus:border-heraldic" />
                <input data-testid="lead-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder={l.email}
                  className="w-full text-sm bg-paper border border-border rounded-sm px-3 py-2 focus:outline-none focus:border-heraldic" />
                <input data-testid="lead-phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder={l.phone}
                  className="w-full text-sm bg-paper border border-border rounded-sm px-3 py-2 focus:outline-none focus:border-heraldic" />
                <div className="flex items-center gap-2">
                  <button data-testid="lead-submit" onClick={submitLead}
                    className="flex-1 bg-heraldic text-white text-sm py-2 rounded-sm hover:bg-heraldic-700 transition-colors">{l.send}</button>
                  <button data-testid="lead-skip" onClick={() => setLeadOpen(false)}
                    className="text-xs text-ink/45 hover:text-ink px-2">{w.skip}</button>
                </div>
              </div>
            ) : (
              <button data-testid="concierge-lead-cta" onClick={() => setLeadOpen(true)}
                className="w-full border-t border-border py-2.5 text-xs font-medium text-heraldic hover:bg-heraldic/5 transition-colors">{l.cta}</button>
            )}
            <div className="border-t border-border p-3 flex gap-2">
              <input data-testid="concierge-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={t.placeholder}
                className="flex-1 text-sm bg-secondary/50 border border-border rounded-sm px-3 py-2 focus:outline-none focus:border-heraldic" />
              <button data-testid="concierge-send" onClick={() => send()} disabled={loading}
                className="bg-heraldic text-white px-3 rounded-sm hover:bg-heraldic-700 transition-colors disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
